import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { ElementDetailDialog } from "../components/ElementDetailDialog";
import { CATEGORY_BG, CATEGORY_LABELS, HAZARD_BG, HAZARD_LABELS } from "../lib/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLUMNS = [
  { key: "n", label: "№", w: "w-14" },
  { key: "s", label: "Symbol", w: "w-20" },
  { key: "name", label: "Name", w: "" },
  { key: "mass", label: "Mass", w: "w-24" },
  { key: "period", label: "Period", w: "w-20" },
  { key: "group", label: "Group", w: "w-20" },
  { key: "cat", label: "Category", w: "w-44" },
  { key: "hazard", label: "Hazard", w: "w-28" },
];

const HAZARD_RANK = { extreme: 4, high: 3, warning: 2, safe: 1, unknown: 0 };

export default function ElementIndex() {
  const [elements, setElements] = useState([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "n", dir: "asc" });
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/elements`).then((r) => setElements(r.data));
  }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = elements;
    if (q) {
      out = out.filter((e) =>
        String(e.n) === q ||
        e.s.toLowerCase().includes(q) ||
        e.name.toLowerCase().includes(q) ||
        CATEGORY_LABELS[e.cat].toLowerCase().includes(q) ||
        HAZARD_LABELS[e.hazard].toLowerCase().includes(q)
      );
    }
    out = [...out].sort((a, b) => {
      let av = a[sort.key], bv = b[sort.key];
      if (sort.key === "hazard") { av = HAZARD_RANK[av] ?? -1; bv = HAZARD_RANK[bv] ?? -1; }
      if (sort.key === "mass") { av = parseFloat(String(av).replace(/[\[\]]/g, "")) || 0; bv = parseFloat(String(bv).replace(/[\[\]]/g, "")) || 0; }
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [elements, query, sort]);

  const toggleSort = (key) =>
    setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  const handleRow = (el) => { setSelected(el); setOpen(true); };

  return (
    <div className="bg-white min-h-screen" data-testid="index-page">
      <section className="border-b-2 border-zinc-950">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-600 mb-3 flex items-center gap-3">
            <span className="w-8 h-px bg-zinc-950" /> Index · 118 Elements
          </div>
          <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-[0.85] tracking-tighter uppercase">
            Sortable<br />
            <span className="bg-[#FFD700] px-2 inline-block">Master Index.</span>
          </h1>
          <p className="mt-6 text-lg text-zinc-700 max-w-xl">
            Search, sort and jump straight into any element&apos;s safety profile. Click any row to open the full hazard sheet.
          </p>
        </div>
      </section>

      <section className="border-b-2 border-zinc-950 bg-zinc-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-center gap-2 border-2 border-zinc-950 bg-white px-3 py-2 max-w-md">
            <Search size={16} strokeWidth={2.5} />
            <input
              data-testid="index-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, symbol, category or hazard…"
              className="w-full bg-transparent border-0 outline-none font-mono text-sm placeholder:text-zinc-500"
            />
            {query && (
              <button
                data-testid="index-clear"
                onClick={() => setQuery("")}
                className="font-mono text-[10px] uppercase tracking-widest underline"
              >Clear</button>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="border-2 border-zinc-950 overflow-x-auto">
          <table className="w-full text-sm" data-testid="index-table">
            <thead className="bg-zinc-950 text-white font-mono text-[10px] uppercase tracking-[0.2em]">
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className={`${c.w} text-left px-3 py-3 border-r border-zinc-700 last:border-r-0 cursor-pointer select-none hover:bg-zinc-800`}
                    onClick={() => toggleSort(c.key)}
                    data-testid={`sort-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {c.label}
                      {sort.key === c.key
                        ? (sort.dir === "asc"
                            ? <ArrowUp size={12} strokeWidth={3} />
                            : <ArrowDown size={12} strokeWidth={3} />)
                        : <ArrowUpDown size={12} strokeWidth={2} className="opacity-30" />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={COLUMNS.length} className="text-center py-12 font-mono text-zinc-600">No matches.</td></tr>
              ) : rows.map((el, i) => (
                <tr
                  key={el.n}
                  data-testid={`index-row-${el.s}`}
                  onClick={() => handleRow(el)}
                  className={`${i % 2 === 0 ? "bg-white" : "bg-zinc-50"} border-t border-zinc-200 cursor-pointer hover:bg-[#FFD700]/30 transition-colors`}
                >
                  <td className="px-3 py-2 font-mono font-bold">{el.n}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center justify-center w-9 h-9 border-2 border-zinc-950 font-heading font-black text-base ${CATEGORY_BG[el.cat]}`}>
                      {el.s}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-bold">{el.name}</td>
                  <td className="px-3 py-2 font-mono">{el.mass}</td>
                  <td className="px-3 py-2 font-mono">{el.period}</td>
                  <td className="px-3 py-2 font-mono">{el.group}</td>
                  <td className="px-3 py-2 text-xs">{CATEGORY_LABELS[el.cat]}</td>
                  <td className="px-3 py-2">
                    <span className={`${HAZARD_BG[el.hazard]} border-2 border-zinc-950 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest font-bold`}>
                      {HAZARD_LABELS[el.hazard]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
          Showing {rows.length} of {elements.length} · Sorted by {COLUMNS.find((c) => c.key === sort.key)?.label} ({sort.dir})
        </div>
      </section>

      <ElementDetailDialog element={selected} open={open} onOpenChange={setOpen} />
    </div>
  );
}
